import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { seedNow } from '@/domain/seedContext';
import { isOpenCameDownIssue } from '@/domain/opsWorklist';
import type { Booking } from '@/domain/types';

function nowIso(): string {
  return seedNow() ?? useSimulationStore.getState().clockIso;
}

/**
 * L5 (tv-hrsz, D10-14): resolve a "did NOT come down" checkout issue on the Ops
 * worklist — stamp the booking's resolved marker (when, and who), saved with the
 * booking, so it drops off the list for good (the server keeps the marker against
 * a stale save). `fallback`: the booking as the Ops view holds it, for a tab whose
 * store hasn't loaded that booking. No-op when already resolved or not an issue.
 */
export function resolveCameDownIssue(
  bookingId: string,
  opts: { by: string; fallback?: Booking },
): Booking | undefined {
  const brain = useBrainStore.getState();
  const booking = brain.bookings[bookingId] ?? opts.fallback;
  if (!booking || !isOpenCameDownIssue(booking)) return undefined;
  const at = nowIso();
  const resolved: Booking = { ...booking, cameDownResolvedAt: at, cameDownResolvedBy: opts.by };
  brain.upsertBooking(resolved);
  brain.appendActivity({
    id: `act-came-down-resolved-${bookingId}`,
    type: 'floor-issue',
    customerId: booking.customerId,
    studioId: booking.studioId,
    artistId: booking.artistId,
    body: `Walk-down issue resolved by ${opts.by}.`,
    timestamp: at,
  });
  return resolved;
}
